import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';

/**
 * Support-contact banner St. Luke asked to surface on the auth screens (login + signup).
 * Phone / email / website mirror the contact block St. Luke uses in its patient emails
 * (Notification-service templates) so patients get one consistent point of contact across
 * email and the portal. St. Luke-only — renders nothing for any other tenant.
 *
 * Pinned to the bottom of the screen (fixed) so it's always visible. An in-flow spacer sized to
 * the bar's measured height (via ResizeObserver) is rendered alongside so the fixed bar never
 * overlaps the form's last row — this matters on mobile, where the contact items stack and the
 * bar is taller than a fixed-height spacer would assume.
 */
const ContactBanner: React.FC = () => {
  const { theme } = useTheme();
  const barRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(0);

  // Keep the in-flow spacer exactly as tall as the fixed bar so it never overlaps the form's
  // last row. offsetHeight covers the initial paint; ResizeObserver re-measures when the items
  // re-wrap (viewport / orientation change). useLayoutEffect corrects the height before paint.
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const measure = () => setBarHeight(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [theme.client]);

  if (theme.client !== 'stluke') return null;

  return (
    <>
      <div aria-hidden style={{ height: barHeight }} />
      <div
        ref={barRef}
        role="region"
        aria-label="St. Luke Community Healthcare support contact"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-[#EBD5DB] bg-[#FBF3F5] px-4 py-2.5 text-sm text-[#6B2C39] shadow-[0_-2px_10px_rgba(0,0,0,0.05)]"
        data-testid="contact-banner"
      >
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-y-1 text-center">
          <span className="font-semibold text-[#71001F]">
            Questions? Contact St. Luke Community Healthcare
          </span>
          <div className="flex flex-col items-center gap-x-6 gap-y-0.5 sm:flex-row sm:flex-wrap sm:justify-center">
            <span>
              Phone:{' '}
              <a
                href="tel:+14066764441"
                aria-label="Call St. Luke Community Healthcare at (406) 676-4441"
                className="font-semibold text-[#71001F] underline underline-offset-2 hover:opacity-80"
              >
                (406) 676-4441
              </a>
            </span>
            <span>
              Email:{' '}
              <a
                href="mailto:info@stlukehealthcare.org"
                className="font-medium text-[#71001F] underline underline-offset-2 hover:opacity-80"
              >
                info@stlukehealthcare.org
              </a>
            </span>
            <span>
              Web:{' '}
              <a
                href="https://stlukehealthcare.org"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-[#71001F] underline underline-offset-2 hover:opacity-80"
              >
                stlukehealthcare.org
              </a>
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactBanner;
